import React, { memo, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  LayoutDashboard, CalendarDays, CalendarRange,
  ChevronLeft, ChevronRight, BarChart2, Settings,
  Plus, RefreshCw, BookOpen, Crown, Dumbbell, Gift, Sun, Moon,
} from 'lucide-react';
import { Z_CLASS } from '../../constants/zIndex';
import { haptic } from '../../shared/utils/haptics';
import { useAppContext } from '../../contexts/AppContext';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * BottomNavBar — Navigation unifiee
 * Ligne 1 : Selecteur de jour (toujours visible) + chevrons semaine
 * Ligne 2 : 3 onglets vue (Dashboard/Jour/Semaine) + actions (sync, stats, settings, +)
 */
const BottomNavBar = memo(function BottomNavBar({
  daysNames,
  weekDates,
  currentDayIndex,
  weekOffset,
  onDaySelect,
  onWeekChange,
  onHomeClick,
  onStatsClick,
  onSettingsClick,
  onAddTask,
  onTimerClick,
  onSyncClick,
  isSyncing = false,
  isGoogleConnected = false,
  level,
  activeTimers = 0,
  checkFrenchHoliday,
  isPro = false,
  onSportClick,
  sportStreak = 0,
  onRewardsClick,
  rewardCoins = 0,
  hasFreeReward = false,
}) {
  const { currentView, setCurrentView } = useAppContext();
  const { isDark, toggleMode, colors } = useTheme();
  const todayIndex = new Date().getDay() === 0 ? 6 : new Date().getDay() - 1;

  const handleNav = useCallback((view) => {
    haptic.tap();
    setCurrentView(view);
    if (view === 'dashboard') onHomeClick?.();
  }, [setCurrentView, onHomeClick]);

  return (
    <motion.nav
      initial={{ y: 60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className={`fixed bottom-0 left-0 right-0 ${Z_CLASS.BOTTOM_NAV_BAR}`}
      style={{
        background: isDark ? 'rgba(8,8,12,0.97)' : 'rgba(250,246,239,0.97)',
        borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(105,82,55,0.12)'}`,
        backdropFilter: 'blur(12px)',
      }}
    >
      {/* Row 1: Day selector — toujours visible */}
      <div className="flex items-center justify-center px-2 pt-1.5 pb-0.5 gap-0.5">
        <button
          onClick={() => { haptic.swipe(); onWeekChange(-1); }}
          className="p-1 text-zinc-600 hover:text-zinc-300 transition-colors"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>

        <div className="flex items-center gap-0.5">
          {daysNames.map((day, idx) => {
            const date = weekDates[idx];
            const isHoliday = checkFrenchHoliday?.(date);
            const isWeekend = idx >= 5 || isHoliday;
            const isToday = weekOffset === 0 && idx === todayIndex;
            const isSelected = currentDayIndex === idx;

            return (
              <DayPill
                key={day}
                letter={day.substring(0, 1)}
                date={date.getDate()}
                isSelected={isSelected}
                isToday={isToday}
                isWeekend={isWeekend}
                isDark={isDark}
                onClick={() => {
                  onDaySelect(idx);
                  if (currentView === 'dashboard') {
                    setCurrentView('daily');
                  }
                }}
              />
            );
          })}
        </div>

        <button
          onClick={() => { haptic.swipe(); onWeekChange(1); }}
          className="p-1 text-zinc-600 hover:text-zinc-300 transition-colors"
        >
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Row 2: View tabs + quick actions — tout dans une seule ligne */}
      <div className="flex items-center justify-between px-3 h-[46px]">
        {/* Left: view tabs */}
        <div className="flex items-center gap-1">
          <TabButton icon={LayoutDashboard} label="Accueil" active={currentView === 'dashboard'} onClick={() => handleNav('dashboard')} isDark={isDark} />
          <TabButton icon={CalendarDays}    label="Jour"    active={currentView === 'daily'}     onClick={() => handleNav('daily')}     isDark={isDark} />
          <TabButton icon={CalendarRange}   label="Semaine" active={currentView === 'weekly'}    onClick={() => handleNav('weekly')}    isDark={isDark} />
          <TabButton icon={BookOpen}        label="Notes"   active={currentView === 'notes'}     onClick={() => handleNav('notes')}     isDark={isDark} />
          {!isPro && (
            <TabButton icon={Crown} label="Pro" active={currentView === 'pricing'} onClick={() => handleNav('pricing')} isDark={isDark} accent />
          )}
        </div>

        {/* Right: quick actions */}
        <div className="flex items-center gap-0.5">
          {isGoogleConnected && (
            <MiniBtn icon={RefreshCw} onClick={onSyncClick} spinning={isSyncing} isDark={isDark} />
          )}
          {onSportClick && (
            <MiniBtn icon={Dumbbell} onClick={onSportClick} badge={sportStreak > 0 ? sportStreak : null} isDark={isDark} />
          )}
          {onRewardsClick && (
            <MiniBtn icon={Gift} onClick={onRewardsClick} badge={hasFreeReward ? '!' : rewardCoins > 0 ? rewardCoins : null} highlight={hasFreeReward} isDark={isDark} />
          )}
          <MiniBtn icon={isDark ? Sun : Moon} onClick={toggleMode} isDark={isDark} />
          <MiniBtn icon={BarChart2} onClick={onStatsClick} badge={level > 1 ? level : null} isDark={isDark} />
          <MiniBtn icon={Settings} onClick={onSettingsClick} isDark={isDark} />
          <MiniBtn icon={Plus} onClick={onAddTask} accent isDark={isDark} />
        </div>
      </div>

      {/* Safe area */}
      <div
        className="bg-transparent"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      />
    </motion.nav>
  );
});

// Tab button with optional label
const TabButton = memo(function TabButton({ icon: Icon, label, active, onClick, accent, isDark }) {
  const inactiveColor = isDark ? 'rgba(255,255,255,0.25)' : 'rgba(41,33,24,0.35)';
  const activeColor   = accent ? '#fbbf24' : (isDark ? '#c4b5fd' : '#7C5CBF');
  const activeBg      = active ? (accent ? 'rgba(234,179,8,0.15)' : isDark ? 'rgba(139,92,246,0.12)' : 'rgba(124,92,191,0.10)') : 'transparent';
  const accentInactive = accent ? (isDark ? 'rgba(251,191,36,0.7)' : 'rgba(180,130,0,0.7)') : inactiveColor;

  return (
    <motion.button
      onClick={onClick}
      className="relative flex items-center gap-1.5 px-3 py-1.5 rounded-xl transition-all"
      style={{
        background: activeBg,
        color: active ? activeColor : accentInactive,
      }}
      whileTap={{ scale: 0.9 }}
    >
      <Icon className="w-[22px] h-[22px]" strokeWidth={active ? 2.2 : 1.5} />
      {active && label && (
        <motion.span
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: 'auto', opacity: 1 }}
          className="text-[13px] font-semibold overflow-hidden whitespace-nowrap"
        >
          {label}
        </motion.span>
      )}
      {active && (
        <motion.div
          layoutId="nav-indicator"
          className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-5 h-0.5 rounded-full"
          style={{ background: isDark ? '#a78bfa' : '#7C5CBF' }}
          transition={{ type: 'spring', stiffness: 400, damping: 30 }}
        />
      )}
    </motion.button>
  );
});

// Day pill
const DayPill = memo(function DayPill({ letter, date, isSelected, isToday, isWeekend, isDark, onClick }) {
  const selectedBg   = isDark ? 'rgba(255,255,255,0.92)' : 'rgba(41,33,24,0.88)';
  const selectedText = isDark ? '#0e0e14' : '#FAF6EF';
  const todayBg      = isDark ? 'rgba(139,92,246,0.18)' : 'rgba(124,92,191,0.12)';
  const todayText    = isDark ? '#c4b5fd' : '#7C5CBF';
  const weekendText  = isDark ? 'rgba(255,255,255,0.18)' : 'rgba(41,33,24,0.22)';
  const normalText   = isDark ? 'rgba(255,255,255,0.35)' : 'rgba(41,33,24,0.45)';

  return (
    <button
      onClick={() => { haptic.tap(); onClick?.(); }}
      className="w-7 h-8 rounded-lg flex flex-col items-center justify-center transition-all"
      style={{
        background: isSelected ? selectedBg : isToday ? todayBg : 'transparent',
        color: isSelected ? selectedText : isToday ? todayText : isWeekend ? weekendText : normalText,
      }}
    >
      <span className="text-[10px] font-medium leading-none opacity-60">{letter}</span>
      <span className="text-[14px] font-bold leading-none">{date}</span>
    </button>
  );
});

// Mini action button
const MiniBtn = memo(function MiniBtn({ icon: Icon, onClick, badge, spinning, accent, highlight, isDark }) {
  const defaultColor  = isDark ? 'rgba(255,255,255,0.25)' : 'rgba(41,33,24,0.40)';
  const accentColor   = isDark ? '#a78bfa' : '#7C5CBF';
  const accentBg      = isDark ? 'rgba(139,92,246,0.15)' : 'rgba(124,92,191,0.10)';
  const highlightBg   = isDark ? 'rgba(234,179,8,0.12)' : 'rgba(180,130,0,0.10)';

  return (
    <button
      onClick={() => { haptic.tap(); onClick?.(); }}
      disabled={spinning}
      className="relative p-1.5 rounded-lg transition-all"
      style={{
        background: accent ? accentBg : highlight ? highlightBg : 'transparent',
        color: accent ? accentColor : highlight ? '#d97706' : spinning ? 'rgba(245,158,11,0.6)' : defaultColor,
      }}
    >
      <Icon className={`w-[18px] h-[18px] ${spinning ? 'animate-spin' : ''}`} />
      {badge && (
        <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-3.5 px-0.5 text-[10px] font-bold rounded flex items-center justify-center"
          style={{ background: highlight ? '#d97706' : accentColor, color: isDark ? '#0e0e14' : '#FAF6EF' }}>
          {badge}
        </span>
      )}
    </button>
  );
});

export { BottomNavBar };
